import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Lock, ChevronRight, ClipboardList, StickyNote, Users, Mail, Phone } from 'lucide-react';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import AdBanner from '../components/AdBanner';
import { fetchLists } from '../lib/lists';

export default function Listas() {
    const navigate = useNavigate();
    const [lists, setLists] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchLists().then((d) => {
            setLists(d);
        }).finally(() => setLoading(false));
    }, []);

    const grouped = {
        elementos: lists.filter((l) => l.type === 'elementos'),
        nota: lists.filter((l) => l.type === 'nota'),
        contactos: lists.filter((l) => l.type === 'contactos'),
    };

    return (
        <div className="min-h-screen bg-surface app-shell pb-32">
            <div className="px-6">
                <TopBar
                    onPremiumClick={() => alert('Próximamente: planes Premium')}
                    onBellClick={() => navigate('/notificaciones')}
                    onMenuClick={() => navigate('/home')}
                />

                <div className="mt-4 flex items-end justify-between">
                    <div>
                        <p className="text-gray-500 text-sm font-medium">Listas</p>
                        <h1 className="text-3xl font-bold text-brand-navy tracking-tight" data-testid="listas-title">Tablero Familiar</h1>
                    </div>
                </div>

                {loading ? (
                    <div className="mt-10 flex justify-center">
                        <div className="w-8 h-8 border-2 border-brand/30 border-t-brand rounded-full animate-spin" />
                    </div>
                ) : (
                    <div className="mt-6 space-y-6">
                        <Section
                            title="Listas de Elementos"
                            icon={ClipboardList}
                            count={grouped.elementos.length}
                            data-testid="section-elementos"
                        >
                            {grouped.elementos.map((l) => (
                                <ElementosCard key={l.id} list={l} onClick={() => navigate(`/listas/${l.id}`)} />
                            ))}
                            {grouped.elementos.length === 0 && <Empty text="Aún no hay listas de elementos." />}
                        </Section>

                        <Section
                            title="Notas Rápidas"
                            icon={StickyNote}
                            count={grouped.nota.length}
                            data-testid="section-notas"
                        >
                            {grouped.nota.map((l) => (
                                <NotaCard key={l.id} list={l} onClick={() => navigate(`/listas/${l.id}`)} />
                            ))}
                            {grouped.nota.length === 0 && <Empty text="Aún no hay notas rápidas." />}
                        </Section>

                        <Section
                            title="Contactos"
                            icon={Users}
                            count={grouped.contactos.length}
                            data-testid="section-contactos"
                        >
                            {grouped.contactos.map((l) => (
                                <ContactosCard key={l.id} list={l} onClick={() => navigate(`/listas/${l.id}`)} />
                            ))}
                            {grouped.contactos.length === 0 && <Empty text="Aún no hay listas de contactos." />}
                        </Section>
                    </div>
                )}
            </div>

            <button
                onClick={() => navigate('/listas/nueva')}
                className="fixed bottom-[150px] left-1/2 -translate-x-1/2 px-6 h-14 bg-brand text-white rounded-full font-bold shadow-brand-cta hover:scale-[1.02] active:scale-[0.97] transition flex items-center gap-2 z-30"
                data-testid="listas-fab-new"
            >
                <Plus className="w-5 h-5" /> Nueva lista
            </button>

            <div className="fixed bottom-[72px] left-1/2 -translate-x-1/2 w-full max-w-[480px] z-20">
                <AdBanner />
            </div>
            <BottomNav />
        </div>
    );
}

function Section({ title, icon: Icon, count, children, ...rest }) {
    return (
        <div {...rest}>
            <div className="flex items-center gap-2 mb-3">
                <Icon className="w-4 h-4 text-gray-500" />
                <h2 className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                    {title} ({count})
                </h2>
            </div>
            <div className="space-y-3">{children}</div>
        </div>
    );
}

function Empty({ text }) {
    return (
        <div className="bg-white/60 border border-dashed border-gray-200 rounded-2xl p-5 text-center text-xs text-gray-400">
            {text}
        </div>
    );
}

function ElementosCard({ list, onClick }) {
    const total = list.progress?.total || 0;
    const completed = list.progress?.completed || 0;
    const percent = list.progress?.percent || 0;
    const preview = (list.items || []).slice(0, 3);
    return (
        <button
            onClick={onClick}
            className="w-full bg-white rounded-2xl shadow-card p-4 text-left hover:shadow-card-md active:scale-[0.99] transition"
            data-testid={`list-card-${list.id}`}
        >
            <div className="flex items-center gap-3">
                <span className="text-2xl">{list.icon}</span>
                <h3 className="text-base font-bold text-brand-navy flex-1 truncate">{list.name}</h3>
                {list.is_private && <Lock className="w-4 h-4 text-gray-400" data-testid={`list-${list.id}-private`} />}
                <ChevronRight className="w-5 h-5 text-gray-300" />
            </div>
            <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-brand rounded-full transition-all" style={{ width: `${percent}%` }} />
            </div>
            <div className="mt-3 space-y-1.5">
                {preview.map((it) => (
                    <div key={it.id} className="flex items-center gap-2 text-sm">
                        <span className={`w-4 h-4 rounded-full border ${it.completed ? 'bg-brand border-brand' : 'border-gray-300'} flex items-center justify-center`}>
                            {it.completed && <span className="block w-2 h-2 bg-white rounded-full" />}
                        </span>
                        <span className={`${it.completed ? 'line-through text-gray-400' : 'text-gray-700'}`}>{it.text}</span>
                    </div>
                ))}
                {total > 3 && <div className="text-xs text-gray-400">+{total - 3} más</div>}
            </div>
            <div className="mt-2 text-xs text-gray-500 font-medium">{completed} de {total} completados</div>
        </button>
    );
}

function NotaCard({ list, onClick }) {
    const preview = (list.note_content || '').split('\n').slice(0, 4).join('\n');
    return (
        <button
            onClick={onClick}
            className="w-full bg-white rounded-2xl shadow-card p-4 text-left hover:shadow-card-md active:scale-[0.99] transition"
            data-testid={`list-card-${list.id}`}
        >
            <div className="flex items-center gap-3">
                <span className="text-2xl">{list.icon}</span>
                <h3 className="text-base font-bold text-brand-navy flex-1 truncate">{list.name}</h3>
                {list.is_private && <Lock className="w-4 h-4 text-gray-400" data-testid={`list-${list.id}-private`} />}
                <ChevronRight className="w-5 h-5 text-gray-300" />
            </div>
            {preview && (
                <pre className="mt-3 text-sm text-gray-700 whitespace-pre-wrap font-sans line-clamp-4">{preview}</pre>
            )}
            <div className="mt-3 flex items-center justify-between text-xs">
                <span className="text-gray-400 flex items-center gap-1.5">
                    <StickyNote className="w-3.5 h-3.5" /> Nota rápida
                </span>
            </div>
        </button>
    );
}

function ContactosCard({ list, onClick }) {
    const preview = (list.contacts || []).slice(0, 3);
    const count = list.contact_count ?? (list.contacts || []).length;
    return (
        <button
            onClick={onClick}
            className="w-full bg-white rounded-2xl shadow-card p-4 text-left hover:shadow-card-md active:scale-[0.99] transition"
            data-testid={`list-card-${list.id}`}
        >
            <div className="flex items-center gap-3">
                <span className="text-2xl">{list.icon}</span>
                <h3 className="text-base font-bold text-brand-navy flex-1 truncate">{list.name}</h3>
                {list.is_private && <Lock className="w-4 h-4 text-gray-400" data-testid={`list-${list.id}-private`} />}
                <ChevronRight className="w-5 h-5 text-gray-300" />
            </div>
            <div className="mt-3 space-y-2">
                {preview.map((c) => (
                    <div key={c.id} className="flex items-center gap-3">
                        <span className="w-8 h-8 rounded-full bg-brand/10 text-brand text-xs font-bold flex items-center justify-center">
                            {(c.name || '?').charAt(0).toUpperCase()}
                        </span>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-brand-navy truncate">{c.name}</div>
                            <div className="text-[11px] text-gray-500 flex items-center gap-2 truncate">
                                {c.email && (<><Mail className="w-3 h-3" />{c.email}</>)}
                                {!c.email && c.phone && (<><Phone className="w-3 h-3" />{c.phone}</>)}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <div className="mt-3 text-xs text-gray-500 font-medium">{count} contacto{count !== 1 && 's'}</div>
        </button>
    );
}
